"use client";

import type { ModuleProductWhy } from "@lib/types";
import { PortableText } from "@portabletext/react";
import { CheckmarkCircleIcon } from "@sanity/icons";

import { ResponsiveImage } from "@/components/shared/ResponsiveImage";
import { Container } from "@/components/ui/container";
import { TextAnimate } from "@/components/ui/text-animate";

type WhyProps = {
  module: ModuleProductWhy;
};

export function Why({ module }: WhyProps) {
  if (!module?.title) return null;

  const hasItems = module.items && module.items.length > 0;
  const richTextContent = module.richTextContent;
  const hasRichText = richTextContent && richTextContent.length > 0;

  if (!hasItems && !hasRichText) return null;

  return (
    <section className="py-padding-section-lg md:mx-padding-section-sm relative">
      <Container className="py-padding-section-lg tablet:items-end tablet:justify-center tablet:min-h-[768px] desktop:min-h-[900px] relative flex flex-col">
        {module.backgroundImage ? (
          <>
            <div className="absolute inset-0 z-0 hidden overflow-hidden rounded-lg md:block">
              <ResponsiveImage src={module.backgroundImage} fill className="object-cover" />
              <div className="absolute inset-0 bg-black/50"></div>
            </div>
            <div className="mb-gap-element-xl md:hidden">
              <div className="relative h-full w-full overflow-hidden rounded-lg">
                <ResponsiveImage
                  src={module.backgroundImage}
                  width={393}
                  height={393}
                  className="h-full w-full object-cover"
                  imageOptions={{ desktopWidth: 393 }}
                  useNextImage
                />
              </div>
            </div>
          </>
        ) : null}

        <div className="relative z-10 max-w-xl">
          <div className="mb-gap-element-xl">
            <TextAnimate
              as="h2"
              animation="slideInLeft"
              by="word"
              className="typography-heading-2 text-white"
              delay={1.0}
            >
              {module.title}
            </TextAnimate>
          </div>

          {hasItems ? (
            <div className="space-y-gap-element-lg pl-padding-element-md">
              {module.items!.map((item, index) => (
                <div key={item._key || index} className="gap-gap-element-md flex">
                  <CheckmarkCircleIcon width={20} height={20} color="white" className="md:mt-1" />

                  <div className="flex-1">
                    <TextAnimate
                      animation="slideInLeft"
                      by="word"
                      className="typography-text-medium-normal text-white"
                      delay={2.0 + index * 0.8}
                    >
                      <PortableText value={item.body} />
                    </TextAnimate>
                  </div>
                </div>
              ))}
            </div>
          ) : hasRichText ? (
            <TextAnimate
              animation="slideInLeft"
              by="word"
              className="typography-text-medium-normal text-white"
              delay={2.0}
            >
              <PortableText value={richTextContent} />
            </TextAnimate>
          ) : null}
        </div>
      </Container>
    </section>
  );
}
